import React, { memo, useCallback, useMemo } from 'react'
import tw from 'twin.macro'
import { useSelector } from 'react-redux'
import { BasicSelect } from 'src/components/shared/form/BasicSelect'
import { BasicTextField } from 'src/components/shared/form/BasicTextField'
import { Button } from 'src/components/shared/Button'
import { webhookDeliveryStatusOptions, webhookEventOptions } from 'src/constants/webhook/constants'
import type { WebhookDeliveryStatus, WebhookEventType } from 'src/constants/webhook/enums'
import { getWebhookSubscriptions } from 'src/selectors/webhook'

/** Local to the filter bar. `'any'` is the unset state for each select. */
export interface WebhookDeliveryFilterValues {
  subscriptionId: string
  status: WebhookDeliveryStatus | 'any'
  eventType: WebhookEventType | 'any'
  resourceId: string
}

interface Props {
  filters: WebhookDeliveryFilterValues
  onChange(filters: WebhookDeliveryFilterValues): void
  onClear(): void
}

const ANY_OPTION = { label: 'Any', value: 'any' }

/** Lines the filter controls up with the 36px header buttons, as MarketsTableToolbar does. */
const HEADER_CONTROL_HEIGHT = 36

/**
 * `BasicSelect` and `BasicTextField` expose only a wrapper `css` prop, so the control's own
 * height is set through a descendant selector on the MUI outlined input they both render.
 */
const filterControlCss = {
  '& .MuiOutlinedInput-root': {
    height: HEADER_CONTROL_HEIGHT,
    alignItems: 'center',
  },
}

const WebhookDeliveriesFiltersComponent = ({ filters, onChange, onClear }: Props) => {
  const subscriptions = useSelector(getWebhookSubscriptions)

  const subscriptionOptions = useMemo(() => {
    return [
      { label: 'All subscriptions', value: 'any' },
      ...subscriptions.map((subscription) => {
        return { label: subscription.callbackUrl, value: subscription.id }
      }),
    ]
  }, [subscriptions])

  const statusOptions = useMemo(() => {
    return [ANY_OPTION, ...webhookDeliveryStatusOptions]
  }, [])

  const eventOptions = useMemo(() => {
    return [ANY_OPTION, ...webhookEventOptions]
  }, [])

  const handleSubscriptionChange = useCallback(
    (changeEvent: React.ChangeEvent<any>) => {
      onChange({ ...filters, subscriptionId: changeEvent.target.value })
    },
    [filters, onChange],
  )

  const handleStatusChange = useCallback(
    (changeEvent: React.ChangeEvent<any>) => {
      onChange({ ...filters, status: changeEvent.target.value })
    },
    [filters, onChange],
  )

  const handleEventTypeChange = useCallback(
    (changeEvent: React.ChangeEvent<any>) => {
      onChange({ ...filters, eventType: changeEvent.target.value })
    },
    [filters, onChange],
  )

  const handleResourceIdChange = useCallback(
    (changeEvent: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ ...filters, resourceId: changeEvent.target.value })
    },
    [filters, onChange],
  )

  const hasFilters =
    filters.subscriptionId !== 'any' ||
    filters.status !== 'any' ||
    filters.eventType !== 'any' ||
    filters.resourceId.trim() !== ''

  // `resourceType` is deliberately not offered: resourceId plus eventType already narrow
  // further, and the backend field stays available for a later need.
  // This is the deliveries table's own white header, matching the markets tab's toolbar.
  return (
    <div
      css={tw`flex flex-row flex-wrap items-end gap-4 rounded-t-lg border-0 border-b border-t border-solid border-shades-100 bg-shades-0 px-4 py-3`}
    >
      <div css={[tw`min-w-[200px] flex-1`, filterControlCss]}>
        <BasicSelect
          name="deliveryStatus"
          label="Result"
          options={statusOptions}
          value={filters.status}
          onChange={handleStatusChange}
        />
      </div>
      <div css={[tw`min-w-[240px] flex-1`, filterControlCss]}>
        <BasicSelect
          name="deliveryEventType"
          label="Event"
          options={eventOptions}
          value={filters.eventType}
          onChange={handleEventTypeChange}
        />
      </div>
      <div css={[tw`min-w-[240px] flex-1`, filterControlCss]}>
        <BasicSelect
          name="deliverySubscriptionId"
          label="Subscription"
          options={subscriptionOptions}
          value={filters.subscriptionId}
          onChange={handleSubscriptionChange}
        />
      </div>
      <div css={[tw`min-w-[200px] flex-1`, filterControlCss]}>
        <BasicTextField<WebhookDeliveryFilterValues>
          name="resourceId"
          label="Resource ID"
          placeholder="Exact ID"
          value={filters.resourceId}
          onChange={handleResourceIdChange}
        />
      </div>
      <Button
        size="small"
        color="white"
        disabled={!hasFilters}
        onClick={onClear}
        data-testid="webhook-deliveries-clear-filters-button"
      >
        Clear
      </Button>
    </div>
  )
}

export const WebhookDeliveriesFilters = memo(WebhookDeliveriesFiltersComponent)
